// =============================================================================
// ui/tabs/BarCard.tsx — the mini horizontal bar list.
// -----------------------------------------------------------------------------
// Extracted from Dashboard.tsx when the Day Overview needed the same card. Two
// tabs each drawing their own bars is how two screens come to disagree about
// what a bar of a given width means — and the scaling rule below is exactly the
// kind of decision that would drift apart silently.
//
// ⚠ THE BARS ARE SCALED TO THE LARGEST MAGNITUDE IN THIS CARD, not to a shared
// scale across cards. Each card answers its own question, and a common scale
// would flatten a four-row card against a five-row one for no gain — the numbers
// are printed beside every bar anyway.
// =============================================================================

import { pnlClass, unitValue } from '../format';
import type { BreakdownRow } from '../../selectors/dashBreakdowns';
import type { PnlUnit } from '../../selectors/pnlUnit';

/**
 * ⚠ `unit` IS REQUIRED, and deliberately not optional-defaulting-to-dollars.
 *
 * The two tabs that draw this card answer the question differently: the Day
 * Overview follows the trader's display unit, and the Dashboard deliberately
 * stays in dollars and says so on screen. A default would let a third caller
 * arrive later and inherit an answer nobody chose for it — which is how this
 * card came to print dollars under a points heading in the first place. A
 * required prop turns that into a compile error.
 */
export function BarCard(
  { title, rows, empty, unit }: {
    title: string; rows: readonly BreakdownRow[]; empty?: string; unit: PnlUnit;
  },
) {
  const max = rows.reduce((m, r) => Math.max(m, Math.abs(r.value)), 0);
  return (
    <div className="card">
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="muted">{empty ?? 'Nothing to show yet.'}</p>
      ) : (
        <div className="mini-bars">
          {rows.map((r) => (
            <div className="mini-bar-row" key={r.key}>
              <span className="mini-bar-k">{r.key}</span>
              <span className="mini-bar-track">
                {/* ⚠ Width from the MAGNITUDE, colour from the SIGN. A zero-value
                    row renders a zero-width bar rather than nothing, so the row
                    still reads as present-and-flat rather than as missing. */}
                <span
                  className={`mini-bar-fill ${r.value >= 0 ? 'up' : 'down'}`}
                  style={{ width: max ? `${(Math.abs(r.value) / max) * 100}%` : '0%' }}
                />
              </span>
              {/* ⚠ The LABEL half. It only tells the truth because the rows were
                  summed in the same unit — see the accessor at the call site.
                  Converting one without the other is worse than converting
                  neither: it prints dollars and calls them points. */}
              <span className={`mini-bar-v ${pnlClass(r.value)}`}>
                {unitValue(r.value, unit)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
